"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.formatAsElementId = exports.capitalizeFirstLetter = exports.formatArrayToCommaAndString = exports.sanitizeString = exports.normalizeWhitespaceAndCase = exports.escapeLikePattern = exports.parsePercentageIntegerValue = exports.spaceToDash = exports.combineAddresses = exports.truncateString = exports.snakeCase = exports.kebabCase = exports.constantCase = exports.capitalCase = void 0;
/**
 * Like sentence case, but with the first letter of each word capitalized. Not the same as PascalCase because this function includes spaces between words.
 *
 * Handles camelCase, PascalCase, kebab-case, snake_case, and CONSTANT_CASE.
 */
const capitalCase = (str) => str
    .replace(/([a-z])([A-Z])/g, '$1 $2') // adds spaces to camelCase and PascalCase
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2') // adds spaces to camelCase and PascalCase
    .replace(/[_-]+/g, ' ') // converts kebab-case and snake_case separators to spaces
    .replace(/[\s]+/g, ' ') // converts multiple spaces to single spaces
    .toLowerCase() // converts CONSTANT_CASE to lower case
    .replace(/(^|\s)([a-z])/g, (match) => match.toUpperCase()); // converts first letter of each word to uppercase
exports.capitalCase = capitalCase;
/**
 * Handles camelCase, PascalCase, snake_case, kebab-case, and sentence case.
 */
const constantCase = (str) => str
    .replace(/([a-z])([A-Z])/g, '$1_$2') // converts camelCase and PascalCase
    .replace(/([A-Z])([A-Z][a-z])/g, '$1_$2') // converts camelCase and PascalCase
    .replace(/[/\s-]+/g, '_') // converts certain separators to underscores
    .replace(/['",.!?]+/g, '') // removes common punctuation
    .toUpperCase();
exports.constantCase = constantCase;
/**
 * Handles camelCase, PascalCase, snake_case, CONSTANT_CASE, and sentence case.
 */
const kebabCase = (str) => str
    .replace(/([a-z])([A-Z])/g, '$1-$2') // converts camelCase and PascalCase
    .replace(/([A-Z])([A-Z][a-z])/g, '$1-$2') // converts camelCase and PascalCase
    .replace(/[_/\s]+/g, '-') // converts certain separators to hyphens
    .replace(/['",.!?]+/g, '') // removes common punctuation
    .toLowerCase();
exports.kebabCase = kebabCase;
const snakeCase = (str) => str
    .replace(/([a-z])([A-Z])/g, '$1_$2') // converts camelCase and PascalCase
    .replace(/([A-Z])([A-Z][a-z])/g, '$1_$2') // converts camelCase and PascalCase
    .toLowerCase();
exports.snakeCase = snakeCase;
/**
 * Truncates a string if it is longer than the specified maximum length. Truncates after last whole word and adds ellipses by default.
 */
const truncateString = (text, maxLength, options = {}) => {
    if (text === undefined || text === null || text.length <= maxLength) {
        return text;
    }
    const optionsMerged = {
        includeEllipses: true,
        truncateWords: false,
        ...options,
    };
    let truncatedText = text.slice(0, maxLength);
    if (!optionsMerged.truncateWords) {
        // Truncate the string to the last space before the maximum length UNLESS the string is a single word
        const lastSpaceIndex = truncatedText.lastIndexOf(' ');
        if (lastSpaceIndex > 0) {
            truncatedText = truncatedText.slice(0, lastSpaceIndex);
        }
    }
    if (optionsMerged.includeEllipses) {
        truncatedText += '...';
    }
    return truncatedText;
};
exports.truncateString = truncateString;
const combineAddresses = (addresses) => addresses
    .filter((address) => !!address)
    .map((address) => address.trim())
    .join(', ');
exports.combineAddresses = combineAddresses;
const spaceToDash = (str) => str.replace(/ /g, '-');
exports.spaceToDash = spaceToDash;
const parsePercentageIntegerValue = (value) => {
    if (!value) {
        return null;
    }
    const trimmed = value.trim().replace(/%$/, '');
    if (!trimmed) {
        return null;
    }
    const integerValue = parseInt(trimmed);
    if (isNaN(integerValue) || integerValue < 0 || integerValue > 100) {
        return null;
    }
    return integerValue;
};
exports.parsePercentageIntegerValue = parsePercentageIntegerValue;
/**
 * Escapes SQL LIKE/ILIKE special characters (%, _, \) and normalizes
 * empty/whitespace-only strings to null. Use before passing user input
 * to queries that build patterns like `'%' || :keyword || '%'`.
 */
const escapeLikePattern = (keyword) => {
    const trimmed = keyword?.trim();
    if (!trimmed)
        return null;
    return trimmed.replace(/[\\%_]/g, '\\$&');
};
exports.escapeLikePattern = escapeLikePattern;
/**
 * Trims, collapses internal whitespace runs to a single space, and lowercases — so strings differing only
 * in casing or spacing compare equal. The mechanical half of a comparison key; what counts as "the same
 * value" is the caller's rule to name and own.
 */
const normalizeWhitespaceAndCase = (str) => str.trim().replace(/\s+/g, ' ').toLowerCase();
exports.normalizeWhitespaceAndCase = normalizeWhitespaceAndCase;
const sanitizeString = (s) => {
    // Remove control characters: 0x00-0x08, 0x0B, 0x0C, 0x0E-0x1F, 0x7F
    // Using RegExp constructor with unicode escapes to avoid linter error
    const pattern = String.raw `[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]`;
    return s.replace(new RegExp(pattern, 'g'), '');
};
exports.sanitizeString = sanitizeString;
const formatArrayToCommaAndString = (arr) => {
    if (arr.length === 0) {
        return '';
    }
    else if (arr.length === 1) {
        return arr[0];
    }
    else if (arr.length === 2) {
        return arr.join(' and ');
    }
    else {
        const lastItem = arr[arr.length - 1];
        const excludeLastItem = arr.slice(0, arr.length - 1);
        return `${excludeLastItem.join(', ')}, and ${lastItem}`;
    }
};
exports.formatArrayToCommaAndString = formatArrayToCommaAndString;
const capitalizeFirstLetter = (string) => string.charAt(0).toUpperCase() + string.slice(1);
exports.capitalizeFirstLetter = capitalizeFirstLetter;
/**
 * - Converts to lowercase
 * - Replaces spaces with dashes
 * - Removes unsupported characters (keeping only letters, numbers, dashes, underscores)
 */
const formatAsElementId = (str) => str
    .toLowerCase()
    .replace(/ /g, '-')
    .replace(/[^a-z0-9-_]/g, '');
exports.formatAsElementId = formatAsElementId;
